import { useState } from 'react';
import { signIn, signUp, resetPassword } from '../services/auth';

export default function AuthScreen({ onAuth, initialView }) {
  const [view,     setView]     = useState(initialView || 'welcome'); // welcome | signup | signin | forgot | confirm
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');

  const go  = (v) => { setView(v); setError(''); };
  const clr = ()  => setError('');

  // ── Sign up ─────────────────────────────────────────────────────────────────
  const handleSignUp = async (e) => {
    e.preventDefault();
    if (password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    setLoading(true); clr();
    const { data, error } = await signUp(email, password);
    setLoading(false);
    if (error) { setError(error.message); return; }
    if (data.session) {
      onAuth(data.user);
    } else {
      go('confirm');
    }
  };

  // ── Sign in ─────────────────────────────────────────────────────────────────
  const handleSignIn = async (e) => {
    e.preventDefault();
    setLoading(true); clr();
    const { data, error } = await signIn(email, password);
    setLoading(false);
    if (error) { setError(error.message); return; }
    onAuth(data.user);
  };

  // ── Forgot password ─────────────────────────────────────────────────────────
  const handleForgot = async (e) => {
    e.preventDefault();
    setLoading(true); clr();
    const { error } = await resetPassword(email);
    setLoading(false);
    if (error) { setError(error.message); return; }
    go('confirm');
  };

  // ── Shared field helpers ─────────────────────────────────────────────────────
  const emailField = (
    <div className="field">
      <label htmlFor="auth-email">Email</label>
      <input
        id="auth-email"
        type="email"
        value={email}
        onChange={e => { setEmail(e.target.value); clr(); }}
        placeholder="you@example.com"
        required
        autoComplete="email"
        autoFocus
      />
    </div>
  );

  const passwordField = (hint, autocomplete = 'current-password') => (
    <div className="field">
      <label htmlFor="auth-password">Password</label>
      <input
        id="auth-password"
        type="password"
        value={password}
        onChange={e => { setPassword(e.target.value); clr(); }}
        placeholder={hint || '••••••••'}
        required
        autoComplete={autocomplete}
      />
    </div>
  );

  // ── Views ───────────────────────────────────────────────────────────────────

  if (view === 'welcome') return (
    <div className="auth-shell">
      <div className="auth-card auth-card--welcome">
        <div className="auth-welcome-wordmark">
          <h1 className="logo">Simmer</h1>
          <p className="tagline-small">impulse shopping is COOKED</p>
        </div>
        <p className="auth-welcome-sub">
          Add something you want to buy. Wait 3 days. See if you still want it.
        </p>
        <div className="auth-welcome-actions">
          <button className="btn-primary auth-cta" onClick={() => go('signup')}>
            Start Simmering
          </button>
          <button className="auth-link-btn" onClick={() => go('signin')}>
            I have an account
          </button>
        </div>
      </div>
    </div>
  );

  if (view === 'confirm') return (
    <div className="auth-shell">
      <div className="auth-card">
        <div className="auth-brand">
          <h1 className="logo">Simmer</h1>
          <p className="tagline-small">impulse shopping is COOKED</p>
        </div>
        <div className="auth-confirm-icon">✉️</div>
        <h2 className="auth-heading">Check your email</h2>
        <p className="auth-confirm-body">
          {password
            ? `We sent a confirmation link to ${email}. Click it to activate your account, then come back to sign in.`
            : `We sent a password reset link to ${email}.`}
        </p>
        <button className="btn-primary auth-submit" onClick={() => go('signin')}>
          Back to sign in
        </button>
      </div>
    </div>
  );

  if (view === 'signup') return (
    <div className="auth-shell">
      <div className="auth-card">
        <div className="auth-brand">
          <h1 className="logo">Simmer</h1>
          <p className="tagline-small">impulse shopping is COOKED</p>
        </div>
        <h2 className="auth-heading auth-heading--editorial">Begin your slow journey.</h2>
        <p className="auth-sub-text">
          Create an account to save your favourite finds and set your simmering pace.
        </p>
        <form className="auth-form" onSubmit={handleSignUp} noValidate>
          {emailField}
          {passwordField('At least 6 characters', 'new-password')}
          {error && <p className="auth-error">{error}</p>}
          <button type="submit" className="btn-primary auth-submit" disabled={loading}>
            {loading ? 'Creating account…' : 'Create Account'}
          </button>
        </form>
        <div className="auth-links">
          <button className="auth-link" onClick={() => go('signin')}>
            Already have an account? Sign in
          </button>
        </div>
        <blockquote className="auth-quote">
          "Patience is the companion of wisdom." — St. Augustine
        </blockquote>
      </div>
    </div>
  );

  if (view === 'signin') return (
    <div className="auth-shell">
      <div className="auth-card">
        <div className="auth-brand">
          <h1 className="logo">Simmer</h1>
          <p className="tagline-small">impulse shopping is COOKED</p>
        </div>
        <h2 className="auth-heading auth-heading--editorial">Welcome back.</h2>
        <p className="auth-sub-text">
          Return to your intentional space. Take a moment to breathe before you begin.
        </p>
        <form className="auth-form" onSubmit={handleSignIn} noValidate>
          {emailField}
          {passwordField()}
          {error && <p className="auth-error">{error}</p>}
          <button type="submit" className="btn-primary auth-submit" disabled={loading}>
            {loading ? 'Entering…' : 'Enter the Space'}
          </button>
        </form>
        <div className="auth-links">
          <button className="auth-link" onClick={() => go('signup')}>
            New here? Begin your journey
          </button>
          <button className="auth-link" onClick={() => go('forgot')}>
            Forgot password?
          </button>
        </div>

        {process.env.NODE_ENV === 'development' && window.location.hostname === 'localhost' && (
          <button
            className="dev-bypass-btn"
            onClick={() => onAuth({ id: 'dev-bypass-user', email: 'dev@localhost' })}
          >
            Skip login (dev only)
          </button>
        )}
      </div>
    </div>
  );

  // forgot
  return (
    <div className="auth-shell">
      <div className="auth-card">
        <div className="auth-brand">
          <h1 className="logo">Simmer</h1>
          <p className="tagline-small">impulse shopping is COOKED</p>
        </div>
        <h2 className="auth-heading">Reset your password</h2>
        <p className="auth-sub-text">
          Enter your email and we'll send you a reset link.
        </p>
        <form className="auth-form" onSubmit={handleForgot} noValidate>
          {emailField}
          {error && <p className="auth-error">{error}</p>}
          <button type="submit" className="btn-primary auth-submit" disabled={loading}>
            {loading ? 'Sending…' : 'Send reset link'}
          </button>
        </form>
        <div className="auth-links">
          <button className="auth-link" onClick={() => go('signin')}>
            Back to sign in
          </button>
        </div>
      </div>
    </div>
  );
}
